/** A protocol's newest promotion in one line: its state, who did it and when, and what it does. */

import { counted } from '@/modules/back-testing/format';
import type { PromotionRecord } from './types';

const when = (iso: string | null): string => (iso ? new Date(iso).toLocaleString() : '');

/** One line on the newest promotion. */
export const promotionLine = (
	record: PromotionRecord | null
): {
	label: string;
	variant: 'success' | 'warning' | 'secondary';
	detail: string;
} => {
	if (!record)
		return {
			label: 'Not promoted',
			variant: 'secondary',
			detail: 'Every reply is a draft and every write asks first.'
		};
	const by = record.promotedByName ?? `user ${record.promotedBy}`;
	const promoted = `Promoted by ${by} on ${when(record.promotedAt)}.`;
	if (record.outOfDate || record.status === 'outdated') {
		return {
			label: 'Out of date',
			variant: 'warning',
			detail: `The protocol was compiled again, so everything is back to drafts and asking first. ${promoted} Re-run back tests and promote again.`
		};
	}
	if (record.status === 'active') {
		const replies = Object.values(record.scenarios).filter((setting) => setting.reply === 'auto').length;
		return {
			label: 'Promoted',
			variant: 'success',
			detail: `${promoted} ${counted(replies, 'reply')} send by themselves; ${
				record.autoWrites.length ? `${record.autoWrites.join(', ')} run without asking` : 'every write asks first'
			}.`
		};
	}
	const ended = record.endedByName ?? (record.endedBy ? `user ${record.endedBy}` : 'the system');
	return {
		label: 'Stopped',
		variant: 'secondary',
		detail: `Stopped by ${ended} on ${when(record.endedAt)}. ${promoted}`
	};
};
